import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { ChangeEvent, useCallback, useState } from 'react'

import PlainButton from 'app/common/ui-next/plain-button'
import { SenderItem } from 'app/dataTypes/transfer/senders'
import { RecipientItem } from 'app/dataTypes/transfer/recipients'
import { Params } from 'app/dataTypes/transfer'
import PlainHelperText from 'app/common/ui-next/plain-helper-text/plain-helper-text'
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue'
import { useCurrencySign } from 'app/common/components-next/currency'
import LineInput from 'app/common/ui/LineInput'

import useCallbackRef from 'app/common/hooks/useCallbackRef'
import IconArrow from './icons/arrow.svg'
import IconSuccess from './icons/success.svg'
import { ControlSubmit } from './ControlSubmit'

import styles from './Control.module.scss';


const cx = cn.bind(styles)
const amountRe = /^\d+(\.|,)?\d{0,2}$/

type Props = {
  sender: SenderItem | null
  recipient: RecipientItem | null
  loading?: boolean
  onSubmit: (params: Params) => Promise<boolean>
  onReset: () => void
}

export const Control = ({
  sender,
  recipient,
  loading,
  onSubmit,
  onReset,
}: Props) => {
  const { t } = useTranslation('finances')
  const currency = useCurrencySign()

  const [success, setSuccess] = useState(false)
  const [amount, setAmount] = useState('')
  const amountNum = parseFloat(amount)
  const validAmount = !Number.isNaN(amountNum)
  const hasAmount = !!amountNum
  const hasMinError = validAmount && !!recipient && amountNum < recipient?.minAmount
  const hasMaxError = validAmount && !!recipient && amountNum > recipient?.maxAmount
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const onSubmitRef = useCallbackRef(onSubmit)

  const handleBeginNew = useCallback(() => {
    setSuccess(false)
    setAmount('')
    onReset()
  }, [onReset])

  const handleAmountChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget
    if (!parseFloat(value)) {
      setAmount('')
    }
    if (!amountRe.test(value)) {
      return
    }
    setError(null)
    setAmount(value.replace(',', '.'))
  }, [])

  const checkErrors = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (hasMinError || hasMaxError) {
      setError(t('transfer_sum_error', 'Неверно указана сумма'))
      return
    }
    setShowConfirmation(true)
  }, [hasMinError, hasMaxError, t])

  const handleSubmit = useCallback(async () => {
    if (!sender || !recipient || !hasAmount) return
    setSubmitting(true)
    const res = await onSubmitRef.current?.({
      senderUID: sender.UID,
      recipientUID: recipient.UID,
      amount: String(amountNum),
    })
    setSubmitting(false)
    if (res) {
      setSuccess(true)
    }
  }, [onSubmitRef, amountNum, hasAmount, sender, recipient])

  return (
    <form className={styles.main} onSubmit={checkErrors}>
      {success
        && <IconSuccess className={styles.success} />}

      <div className={styles.form}>
        <div className={styles.grow}>
          <IconArrow className={cx(styles.arrow, { active: amount })} />
        </div>

        <div className={styles.inputContainer}>
          {!sender || !recipient
            ? <div className={styles.choose}>
              {t('transfer_select_invite', 'Выберите от кого и кому сделать перевод')}
            </div>

            : <LineInput
              className={styles.input}
              onChange={handleAmountChange}
              value={amount}
              placeholder={success
                ? t('transfer_placeholder_success', 'Переведено')
                : t('transfer_placeholder_enter', 'Введите сумму')}
              disabled={success || loading}
              icon={amount ? currency : undefined}
              isError={!!error}
              errorText={error || ' '}
              errorClassName={styles.error}
              bold
            />}

          <PlainHelperText className={styles.maxmin} error={hasMaxError || hasMinError}>
            {recipient
              && typeof recipient.minAmount === 'number'
              && typeof recipient.maxAmount === 'number'
              && !loading
              && t('maxmin_refill', {
                min: toFixedValueCurrency(recipient.minAmount, false, currency),
                max: toFixedValueCurrency(recipient.maxAmount, false, currency),
              })}
          </PlainHelperText>
        </div>

        <div className={styles.grow}>
          <IconArrow className={cx(styles.arrow, { active: amount })} />
        </div>
      </div>

      <div className={styles.btnContainer}>
        {!success
          && <ControlSubmit
            className={styles.transferBtn}
            transfering={submitting}
            showConfirmation={showConfirmation}
            setShowConfirmation={setShowConfirmation}
            disabled={loading || !amount || !sender || !recipient}
            onSubmit={handleSubmit}
          />}

        {success
          && <PlainButton
            className={styles.transferBtn}
            onClick={handleBeginNew}
          >
            {t('transfer_bt_new', 'Новый перевод')}
          </PlainButton>}
      </div>
    </form>
  )
}
